import { useCallback } from "react";
import useApiClient from "./ApiClient";
import { PagedResults } from "../types/PagedResults";
import { Campaign, Reward, Rule, RuleGroup } from "../types/Campaigns";
import { HttpError } from "../types/Error";
import { UserSummary } from "../types/User";

const useCampaignsService = () => {
  const { get, post, postFormData, putFormData, put, delete: del } = useApiClient();

  const getCampaign = async (id: string) => {
    try {
      const result = await get(`/admin/campaigns/${id}`);
      return result;
    } catch (error) {
      console.error("Error fetching campaign", error);
      throw error;
    }
  };

  const searchCampaigns = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      itemsPerPage: number = 50
    ): Promise<PagedResults<Campaign>> => {
      try {
        const results = await post<PagedResults<Campaign>>(
          "/admin/campaigns/search",
          { searchTerm, page, itemsPerPage }
        );
        // Check if the response is an error.
        if ((results as HttpError).errorCode) {
          throw (results as HttpError).description;
        }

        return results as PagedResults<Campaign>;
      } catch (error) {
        console.error("Error searching campaigns", error);
        throw error;
      }
    },
    [post]
  );

  const createCampaign = useCallback(
    async (campaignData: FormData): Promise<Campaign | HttpError> => {
      try {
        const result = await postFormData<Campaign>(
          "/admin/campaigns",
          campaignData
        );
        return result;
      } catch (error) {
        console.error("Error creating campaign", error);
        throw error;
      }
    },
    [postFormData]
  );

  const updateCampaign = useCallback(
    async (id: string, campaignData: FormData): Promise<Campaign | HttpError> => {
      try {
        const result = await putFormData<Campaign>(
          `/admin/campaigns/${id}`,
          campaignData
        );
        return result;
      } catch (error) {
        console.error("Error updating campaign", error);
        throw error;
      }
    },
    [putFormData]
  );

  const approveCampaign = useCallback(
    async (id: string): Promise<Campaign | HttpError> => {
      try {
        const result = await post<Campaign>(`/admin/campaigns/${id}/approve`, {});
        return result;
      } catch (error) {
        console.error("Error approving campaign", error);
        throw error;
      }
    },
    [post]
  );

  const deleteCampaign = useCallback(
    async (id: string): Promise<boolean | HttpError> => {
      try {
        await del(`/admin/campaigns/${id}`);
        return true;
      } catch (error) {
        console.error("Error deleting campaign", error);
        throw error;
      }
    },
    [del]
  );

  const getRewardsForCampaign = useCallback(
    async (campaignId: string): Promise<Reward[] | HttpError> => {
      try {
        const result = await get(`/admin/campaigns/${campaignId}/rewards`);
        return result;
      } catch (error) {
        console.error("Error fetching rewards for campaign", error);
        throw error;
      }
    },
    [get]
  );

  const getRuleGroupsForReward = useCallback(
    async (campaignId: string, rewardId: string): Promise<RuleGroup[] | HttpError> => {
      try {
        const result = await get(`/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups`);
        return result;
      } catch (error) {
        console.error("Error fetching rule groups for reward", error);
        throw error;
      }
    },
    [get]
  );

  const getRulesForRuleGroup = useCallback(
    async (campaignId: string, rewardId: string, ruleGroupId: string): Promise<Rule[] | HttpError> => {
      try {
        const result = await get(`/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups/${ruleGroupId}/rules`);
        return result;
      } catch (error) {
        console.error("Error fetching rules for rule group", error);
        throw error;
      }
    },
    [get]
  );

  const deleteReward = useCallback(
    async (campaignId: string, rewardId: string): Promise<boolean | HttpError> => {
      try {
        await del(`/admin/campaigns/${campaignId}/rewards/${rewardId}`);
        return true;
      } catch (error) {
        console.error("Error deleting reward", error);
        throw error;
      }
    },
    [del]
  );

  const createReward = useCallback(
    async (campaignId: string, rewardData: FormData): Promise<Reward | HttpError> => {
      try {
        const result = await postFormData<Reward>(
          `/admin/campaigns/${campaignId}/rewards`,
          rewardData
        );
        return result;
      } catch (error) {
        console.error("Error creating reward", error);
        throw error;
      }
    },
    [postFormData]
  );

  const updateReward = useCallback(
    async (campaignId: string, rewardId: string, rewardData: FormData): Promise<Reward | HttpError> => {
      try {
        const result = await putFormData<Reward>(
          `/admin/campaigns/${campaignId}/rewards/${rewardId}`,
          rewardData
        );
        return result;
      } catch (error) {
        console.error("Error updating reward", error);
        throw error;
      }
    },
    [putFormData]
  );

  const createRuleGroup = useCallback(
    async (campaignId: string, rewardId: string, ruleGroupData: RuleGroup): Promise<RuleGroup | HttpError> => {
      try {
        const result = await post<RuleGroup>(
          `/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups`,
          ruleGroupData
        );
        return result;
      } catch (error) {
        console.error("Error creating rule group", error);
        throw error;
      }
    },
    [postFormData]
  );

  const createRule = useCallback(
    async (campaignId: string, rewardId: string, ruleGroupId: string, ruleData: Rule): Promise<Rule | HttpError> => {
      try {
        const result = await post<Rule>(
          `/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups/${ruleGroupId}/rules`,
          ruleData
        );
        return result;
      } catch (error) {
        console.error("Error creating rule", error);
        throw error;
      }
    },
    [postFormData]
  );

  const updateRuleGroup = useCallback(  
    async (campaignId: string, rewardId: string, ruleGroupId: string, ruleGroupData: RuleGroup): Promise<RuleGroup | HttpError> => {  
      try {  
        const result = await put<RuleGroup>(
          `/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups/${ruleGroupId}`,  
          ruleGroupData  
        );  
        return result;  
      } catch (error) {  
        console.error("Error updating rule group", error);  
        throw error;  
      }
    },  
    [putFormData]  
  );

  const updateRule = useCallback(
    async (campaignId: string, rewardId: string, ruleGroupId: string, ruleId: string, ruleData: Rule): Promise<Rule | HttpError> => {
      try {
        const result = await put<Rule>(
          `/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups/${ruleGroupId}/rules/${ruleId}`,
          ruleData
        );
        return result;
      } catch (error) {
        console.error("Error updating rule", error);
        throw error;
      }
    },
    [putFormData]
  );

  const deleteRuleGroup = useCallback(
    async (campaignId: string, rewardId: string, ruleGroupId: string): Promise<boolean | HttpError> => {
      try {
        await del(`/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups/${ruleGroupId}`);
        return true;
      } catch (error) {
        console.error("Error deleting rule group", error);
        throw error;
      }
    },
    [del]
  );

  const deleteRule = useCallback(
    async (campaignId: string, rewardId: string, ruleGroupId: string, ruleId: string): Promise<boolean | HttpError> => {
      try {
        await del(`/admin/campaigns/${campaignId}/rewards/${rewardId}/rulegroups/${ruleGroupId}/rules/${ruleId}`);
        return true;
      } catch (error) {
        console.error("Error deleting rule", error);
        throw error;
      }   
    },
    [del]
  );

  const getPreviewUsersForCampaign = useCallback(
    async (campaignId: string): Promise<UserSummary[]> => {
      try {
        const result = await get<UserSummary[]>(`/admin/campaigns/${campaignId}/previewusers`);
        // Check if the response is an error.
        if ((result as unknown as HttpError).errorCode) {
          throw (result as unknown as HttpError).description;
        }

        return result as UserSummary[];
      } catch (error) {
        console.error("Error fetching preview users for campaign", error);
        throw error;
      }
    },
    [get]
  );

  const addPreviewUserToCampaign = useCallback(
    async (campaignId: string, userId: string): Promise<void> => {
      try {
        const result = await post(`/admin/campaigns/${campaignId}/previewusers`, { userId });
        // The API client returns errors rather than throwing them.
        if ((result as HttpError)?.errorCode) {
          throw (result as HttpError).description;
        }
      } catch (error) {
        console.error("Error adding preview user to campaign", error);
        throw error;
      }
    },
    [post]
  );

  const removePreviewUserFromCampaign = useCallback(
    async (campaignId: string, userId: string): Promise<void> => {
      try {
        const result = await del(`/admin/campaigns/${campaignId}/previewusers/${userId}`);
        // The API client returns errors rather than throwing them.
        if ((result as HttpError)?.errorCode) {
          throw (result as HttpError).description;
        }
      } catch (error) {
        console.error("Error removing preview user from campaign", error);
        throw error;
      }
    },
    [del]
  );

  return {
    searchCampaigns,
    getCampaign,
    createCampaign,
    updateCampaign,
    approveCampaign,
    deleteCampaign,
    getRewardsForCampaign,
    getRuleGroupsForReward,
    getRulesForRuleGroup,
    deleteReward,
    createReward,
    updateReward,
    createRuleGroup,
    createRule,
    updateRuleGroup,
    updateRule,
    deleteRule,
    deleteRuleGroup,
    getPreviewUsersForCampaign,
    addPreviewUserToCampaign,
    removePreviewUserFromCampaign,
  };
};

export default useCampaignsService;
